import React, {
	useContext, useState, useMemo, useCallback,
} from 'react';

import { ListContext } from '../ListContext';


// Hook providing bulk functionality
const useBulkActions = ({ items, canSelectItemPredicate, initialAllSelected }) => {
	// NOTE: This hook neatly does not need to know whether or not the parent query is 'loading'. This is because
	// when loading we always pass items as an empty array, which breaks no functionality below. If this was decided
	// to be changed, a neat way to handle this here would be to have a simple conditional at the start of this hook,
	// returning { allSelected: false, toggleListItems: () => {}} and so on.

	const [selected, setSelected] = useState(initialAllSelected ? items.filter(canSelectItemPredicate) : []);

	// Assumes selected objects have an id field
	const selectedAsArrayofIDs = useMemo(() => selected.map(item => item.id), [selected]);

	const toggleItem = useCallback(item => {
		if (selectedAsArrayofIDs.includes(item.id)) {
			setSelected(selected.filter(selectedItem => item.id !== selectedItem.id));
		}
		else {
			setSelected([...selected, item]);
		}
	}, [selectedAsArrayofIDs, setSelected, selected]);

	const toggleItemCreator = useCallback(item => () => {
		if (selectedAsArrayofIDs.includes(item.id)) {
			setSelected(selected.filter(selectedItem => item.id !== selectedItem.id));
		}
		else {
			setSelected([...selected, item]);
		}
	}, [selectedAsArrayofIDs, selected, setSelected]);

	// Is all IDs on screen (not off the page)
	const allIDs = useMemo(() => items.map(item => item.id), [items]);

	// Whether all items are selected
	const allSelected = useMemo(() => {

		let processedItems = items;
		if (canSelectItemPredicate && items) {
			processedItems = items.filter(canSelectItemPredicate);
		}

		if (!processedItems?.length) {
			return false; // Handles edge case where there are zero items and the all checkbox becomes filled
		}

		return processedItems.every(item => {
			return selectedAsArrayofIDs.includes(item.id);
		});
	}, [items, canSelectItemPredicate, selectedAsArrayofIDs]);


	const anySelected = selectedAsArrayofIDs.length > 0;

	const toggleAllListItems = useCallback(() => {

		if (allSelected) {
			setSelected([]);
		}
		else {
			if (canSelectItemPredicate) {
				setSelected(items.filter(canSelectItemPredicate));
			}
			else {
				setSelected(items);
			}
		}
	}, [allSelected, setSelected, items, canSelectItemPredicate]);

	// Same as toggleAllListItems, except only applies to filtered items
	const toggleAllFilteredItems = useCallback(() => {
		// AllSelected is actually "filtered selected". If they are all selected, remove on screen / filtered items (don't fully reset).
		if (allSelected) {
			// Just remove visible items from selected
			setSelected(selected.filter(item => !allIDs.includes(item.id)));
		}
		else {
			const newItems = items.filter(item => !selectedAsArrayofIDs.includes(item.id));
			setSelected([...selected, ...newItems]);
		}
	}, [allSelected, selected, allIDs, items, selectedAsArrayofIDs]);

	const resetSelected = useCallback(() => setSelected([]), [setSelected]);

	const selectedCount = selectedAsArrayofIDs.length;

	const itemIsSelected = useCallback(item => selectedAsArrayofIDs.includes(item.id), [selectedAsArrayofIDs]);

	return {
		selected,
		selectedAsArrayofIDs,
		allSelected,
		anySelected,
		toggleItem,
		toggleItemCreator,
		toggleAllListItems,
		toggleAllFilteredItems,
		setSelected,
		resetSelected,
		selectedCount,
		itemIsSelected,
	};
};


export default function (WrappedComponent) {
	const ReturnedComponent = function (props) {
		const listContext = useContext(ListContext);
		const { items } = listContext;
		const { canSelectItemPredicate, initialAllSelected } = props;
		// Custom hook providing bulk action functionality
		const {

			selected,						// Array of selected items
			selectedAsArrayofIDs,			// Array of selected ids
			allSelected,					// Whether all ON SCREEN items are selected
			anySelected,					// Whether any ON SCREEN item is selected
			toggleItem,						// Checkbox toggle function
			toggleItemCreator,				// Function to create checkbox toggle function
			toggleAllListItems,				// Toggles all items, ignoring presence of off-screen items
			toggleAllFilteredItems,			// Toggles all items, taking into client off-screen items
			setSelected,					// Direct function to select items
			resetSelected,					// Resets selected state
			selectedCount,					// How many items are selected
			itemIsSelected,					// Function to determine if a list item is selected

		} = useBulkActions({ items, canSelectItemPredicate, initialAllSelected });

		const context = useMemo(() => ({
			...listContext,	// Adds to the old list context

			selected,
			selectedAsArrayofIDs,
			allSelected,
			anySelected,
			toggleItem,
			toggleItemCreator,
			toggleAllListItems,
			toggleAllFilteredItems,
			setSelected,
			resetSelected,
			selectedCount,
			itemIsSelected,
			canSelectItemPredicate,
			initialAllSelected,

			hasBulkFunctionality: true,
		}), [listContext, selected, selectedAsArrayofIDs, allSelected, anySelected, toggleItem, toggleItemCreator, toggleAllListItems, toggleAllFilteredItems, setSelected, resetSelected, selectedCount, itemIsSelected, canSelectItemPredicate, initialAllSelected]);

		return (
			// eslint-disable-next-line react/jsx-filename-extension
			<ListContext.Provider value={context}>
				<WrappedComponent {...props} />
			</ListContext.Provider>
		);
	};

	return ReturnedComponent;
}


